<template>
  <v-layout row wrap>
    <AuditItemDialog
      :dialogAuditItem.sync="dialogAuditItem"
      v-if="dialogAuditItem"
    />
    <AuditFindingDialog
      :current-audit-item="currentAuditItem"
      :dialogDeviation.sync="dialogDeviation"
      v-if="dialogDeviation"
    />
    <AuditMeasureDialog
      :dialogMeasure.sync="dialogMeasure"
      v-if="dialogMeasure"
    />

    <AuditProcessContainer>
      <AuditProgress slot="container_progress" :auditProgress="auditProgress" />

      <AuditSummaryDialog
        slot="container_progress"
        :current-audit="currentAudit"
      />

      <GroupableItemList
        slot="container_content"
        :list-items="auditItems"
        list-config-key="auditWrapup"
        :manipulators="auditItemManipulators"
      >
        <template v-slot:default="{ item }">
          <AuditItemRow :key="item.id">
            <ItemLayout
              slot="question"
              :severity="getSeverity(item.question.importance, 'audititem')"
              highlight-field-display-name="value"
              type="question"
            >
              <AuditQuestionComponent
                slot="content"
                :question="item.question"
                :standard-id="currentAudit.standard"
              />

              <AuditProcessMenu
                v-if="hover"
                slot-scope="{ hover }"
                slot="actions"
                :context="{ ref: { auditId, auditItemId: item.id } }"
                :actions="getAuditItemActions"
                @itemdelete="deleteCurrentAuditItem(auditId, item.id)"
                @itemupdate="updateCurrentAuditItem(auditId, item.id, item)"
              />
            </ItemLayout>

            <AuditFindings
              v-for="deviation in orderFindingsByImportance(item.deviations)"
              :key="deviation.id"
              slot="findings"
            >
              <ItemLayout
                slot="finding"
                :severity="getSeverity(deviation.importance, 'finding')"
                highlight-field-display-name="value"
                type="finding"
              >
                <AuditFindingComponent
                  slot="content"
                  :context="{
                    ref: {
                      auditId,
                      auditItemId: item.id,
                      findingId: deviation.id
                    }
                  }"
                  :deviation="deviation"
                />

                <template slot-scope="{ hover }" slot="actions">
                  <AuditProcessMenu
                    v-if="hover"
                    :actions="
                      item.deactivated
                        ? getDeactivatedAuditItemActions
                        : getFindingActions
                    "
                    @itemupdate="openFindingDialog"
                    @itemcamera="callCamera"
                    @itemattach="pickFile(auditId, item.id, deviation.id)"
                    :context="{
                      ref: {
                        auditId,
                        auditItemId: item.id,
                        findingId: deviation.id
                      }
                    }"
                  />
                </template>
              </ItemLayout>

              <template
                v-if="!item.deactivated && !item.completed && !item.checked"
              >
                <ItemLayout
                  v-for="(measure, measure_index) in getFindingMeasureMapping[
                    deviation.id
                  ]"
                  :key="measure_index"
                  slot="measures"
                  :severity="getSeverity(measure.measuretype, 'measure')"
                  highlight-field-display-name="short"
                >
                  <AuditMeasureComponent slot="content" :measure="measure" />

                  <AuditProcessMenu
                    v-if="hover"
                    slot-scope="{ hover }"
                    slot="actions"
                    :actions="getMeasureActions"
                    :context="{
                      ref: {
                        auditId,
                        auditItemId: item.id,
                        findingId: deviation.id,
                        measureId: measure.id
                      }
                    }"
                    @itemdelete="
                      deleteCurrentMeasure(
                        auditId,
                        item.id,
                        deviation.id,
                        measure.id
                      )
                    "
                    @itemupdate="
                      updateCurrentMeasure(
                        auditId,
                        item.id,
                        deviation.id,
                        measure.id,
                        measure
                      )
                    "
                  />
                </ItemLayout>
              </template>

              <CreateMeasure
                slot="createmeasure"
                v-if="!item.deactivated && !item.completed && !item.checked"
                :actions="actionsCreateMeasure"
                @itemcreate="
                  createCurrentMeasureSlow(auditId, item.id, deviation.id)
                "
                @itemcreate_quick="
                  createCurrentMeasureQuick(
                    $event,
                    auditId,
                    item.id,
                    deviation.id
                  )
                "
              />
            </AuditFindings>

            <CreateFinding
              slot="createfinding"
              v-if="!item.deactivated && !item.completed && !item.checked"
              :audit-id="auditId"
              :audit-item-id="item.id"
              dialog
              @open-finding-dialog="openFindingDialog"
              @attachment-create-finding="createCurrentDeviationAttach"
            />
          </AuditItemRow>
        </template>
      </GroupableItemList>
    </AuditProcessContainer>

    <input
      type="file"
      style="display:none;"
      ref="image"
      accept="image/*"
      @change="onFilePicked"
    />

    <input
      type="file"
      style="display:none;"
      ref="quickattach"
      accept="image/*"
      @change="onAttachmentPicked"
    />
  </v-layout>
</template>

<i18n>
{
  "de": {
    "audititem_header": "Auditfrage",
    "auditfinding_header": "Feststellung",
    "auditmeasure_header": "Maßnahme",
    "auditquestion": "Auditfrage",
    "auditfinding": "Feststellung",
    "category": "Kategorie",
    "chapter" : "Kapitel",
    "measure": "Maßnahme",
    
    "create_measure": "Maßnahme hinzufügen",
    "finding_9": "Hauptabweichung",
    "finding_3": "Nebenabweichung",
    "finding_1": "Anmerkung",
    "finding_0": "Bemerkung"
  },
  "en": {
    "auditquestion": "Auditfrage",
    "auditfinding": "Auditfinding",
    "category": "Category",
    "chapter": "Kapitel",
    "measure": "Measure",
    
    "create_measure": "Create Measure",
    "finding_9": "Major Nonconformity",
    "finding_3": "Nonconformity",
    "finding_1": "Anmerkung",
    "finding_0": "Remark"
  }
}
</i18n>


<script>
import _ from "lodash";
// Dialogs
import AuditSummaryDialog from "@/components/dialogs/AuditSummaryDialog.vue";
import AuditItemDialog from "@/components/dialogs/AuditItemDialog.vue";
import AuditFindingDialog from "@/components/dialogs/AuditFindingDialog.vue";
import AuditMeasureDialog from "@/components/dialogs/AuditMeasureDialog.vue";

// Layouts
import AuditProcessContainer from "@/components/layouts/BaseLayouts/AuditProcessContainer.vue";
import AuditItemRow from "@/components/layouts/AuditWrapUp/AuditItemRow.vue";
import AuditFindings from "@/components/layouts/AuditWrapUp/AuditFindings.vue";
import ItemLayout from "@/components/layouts/BaseLayouts/ItemLayout.vue";
import GroupableItemList from "@/components/layouts/BaseLayouts/GroupableItemList";

// Controls/Snippets
import AuditProgress from "@/components/snippets/AuditProgress.vue";
import AuditProcessMenu from "@/components/controls/AuditProcessMenu.vue";
import CreateFinding from "@/components/controls/CreateFinding.vue";
import CreateMeasure from "@/components/controls/CreateMeasure.vue";

// Components
import AuditQuestionComponent from "@/components/widgets/AuditQuestionComponent.vue";
import AuditFindingComponent from "@/components/widgets/AuditFindingComponent.vue";
import AuditMeasureComponent from "@/components/widgets/AuditMeasureComponent.vue";

// Mixins
import FileUploaderMixin from "@/components/mixins/FileUploaderMixin.vue";
import AuditItemListMixin from "@/components/mixins/AuditItemListMixin";

import { mapActions, mapGetters } from "vuex";
import { api } from "@/store/modules/audits";
import { api as confApi } from "@/store/modules/configuration";

import moment from "moment";

export default {
  components: {
    AuditSummaryDialog,
    AuditItemDialog,
    AuditFindingDialog,
    AuditMeasureDialog,
    AuditProcessContainer,
    AuditItemRow,
    ItemLayout,
    AuditFindings,
    AuditProgress,
    AuditQuestionComponent,
    AuditFindingComponent,
    AuditMeasureComponent,
    AuditProcessMenu,
    CreateFinding,
    CreateMeasure,
    GroupableItemList
  },
  data() {
    return {
      dialogAuditItem: false,
      dialogDeviation: false,
      dialogMeasure: false,
      actionsCreateMeasure: {
        primary: {
          id: "1",
          event: "itemcreate_quick",
          text: this.$i18n.t("create_measure")
        },
        secondary: [
          {
            id: "1",
            event: "itemcreate",
            icon: "edit",
            text: this.$i18n.t("create_measure")
          }
        ]
      }
    };
  },
  mixins: [AuditItemListMixin, FileUploaderMixin],
  computed: {
    ...mapGetters(confApi.namespace, [
      confApi.getters.measure,
      confApi.getters.getFindingActions,
      confApi.getters.getAuditItemActions,
      confApi.getters.getDeactivatedAuditItemActions,
      confApi.getters.getMeasureActions
    ]),
    ...mapGetters(api.namespace, [api.getters.getFindingMeasureMapping]),

    MeasureResponible() {
      return this.currentAudit.responsible || null;
    }
  },
  methods: {
    ...mapActions(api.namespace, [
      api.actions.setAuditId,
      api.actions.setAuditItemId,
      api.actions.setDeviationId,
      api.actions.setMeasureId,
      api.actions.setDialogAction,
      api.actions.setDialogItem,
      api.actions.createMeasure,
      api.actions.deleteMeasure
    ]),
    createEmptyMeasure(AutorId, AuditId, AuditItemId, FindingId, AssignTo) {
      const start_status = this.measure.workflow.status.find(
        status => status.start
      );
      console.assert(start_status, "missing measure workflow start status");
      return {
        auditRef: {
          id: AuditId,
          name: this.currentAudit.name
        },
        audit_id: AuditId,
        audititem_id: AuditItemId,
        deviation_id: FindingId,
        description: "",
        createdDate: moment().format("YYYY-MM-DD"),
        text: "",
        priority: false,
        status: start_status.id,
        autor: AutorId,
        assignedTo: AssignTo,
        measuretype: "0",
        dueDate: moment()
          .add(14, "d")
          .format("YYYY-MM-DD")
      };
    },
    createCurrentMeasureSlow(audit_id, audititem_id, deviation_id) {
      console.log("Setting Object Path");
      this.setAuditId(audit_id);
      this.setAuditItemId(audititem_id);
      this.setDeviationId(deviation_id);
      this.setDialogAction("create");

      const measure = this.createEmptyMeasure(
        this.$user.id(),
        audit_id,
        audititem_id,
        deviation_id,
        this.MeasureResponible
      );
      console.log("Create Measure", measure);
      this.setDialogItem(measure);
      this.dialogMeasure = true;
    },
    createCurrentMeasureQuick(arg, audit_id, audititem_id, deviation_id) {
      const measure = this.createEmptyMeasure(
        this.$user.id(),
        audit_id,
        audititem_id,
        deviation_id,
        this.MeasureResponible
      );
      console.log(
        "createCurrentMeasureQuick",
        arg,
        this.measure.data.types,
        this.measure.data.types.find(type => type.id === arg.type)
      );
      const placeholder_text = _.get(
        this.measure.data.types.find(type => type.id === arg.type),
        "placeholder_text",
        "Platzhalter für eine Maßnahme"
      );

      measure.text = arg.text || placeholder_text;
      measure.measuretype = arg.type || "0";

      console.log("Create Measure", measure);

      let payload = {
        audit_id: audit_id,
        audititem_id: audititem_id,
        deviation_id: deviation_id,
        data: measure
      };

      console.log(payload);

      this.createMeasure(payload);
    },
    updateCurrentMeasure(
      audit_id,
      audititem_id,
      deviation_id,
      measure_id,
      measure
    ) {
      console.log("Setting Object Path");
      this.setAuditId(audit_id);
      this.setAuditItemId(audititem_id);
      this.setDeviationId(deviation_id);
      this.setMeasureId(measure_id);
      this.setDialogAction("update");

      console.log("Update Measure");
      this.setDialogItem({ ...measure });
      this.dialogMeasure = true;
    },
    deleteCurrentMeasure(audit_id, audititem_id, deviation_id, measure_id) {
      const payload = {
        audit_id: audit_id,
        audititem_id: audititem_id,
        deviation_id: deviation_id,
        measure_id: measure_id
      };

      console.log("Delete measure", payload);

      confirm("Are you sure you want to delete this measure?") &&
        this.deleteMeasure(payload);
    }
  }
};
</script>
